import { useState, useEffect } from "react";
import api from "@/lib/api";

/**
 * Modal preview/modifica della mail di conferma iscrizione, con conferma stato.
 * Props:
 *   - iscrizione: { _id, cognome, nome, corso_codice, corso_descrizione }
 *   - onClose(): chiamato in chiusura senza invio
 *   - onSent(result): chiamato dopo invio riuscito (result è la risposta API)
 */
export default function MailConfermaModal({ iscrizione, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toText, setToText] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sources, setSources] = useState([]);
  const [step, setStep] = useState("compose"); // compose | confirming | sending | done
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!iscrizione?._id) return;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await api.get(`/mensa-matrice/extra-iscrizioni/${iscrizione._id}/prepara-mail-conferma`);
        setToText((r.data.to_list || []).join(", "));
        setSubject(r.data.subject || "");
        setBody(r.data.body || "");
        setSources(r.data.sources || []);
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || e.message || "Errore sconosciuto";
        console.error("MailConferma prepare failed", { iid: iscrizione?._id, status, detail, response: e.response });
        setErr(`${detail}${status ? ` (HTTP ${status})` : ""}`);
      } finally { setLoading(false); }
    })();
  }, [iscrizione?._id]);

  const invia = async () => {
    // Step 1: conferma cambio stato
    if (step === "compose") {
      setStep("confirming");
      return;
    }
    // Step 2: esecuzione invio
    setSending(true); setErr(null);
    try {
      const to_list = toText.split(/[,;]/).map(s => s.trim()).filter(Boolean);
      if (to_list.length === 0) { setErr("Inserire almeno un destinatario"); setSending(false); return; }
      const r = await api.post(`/mensa-matrice/extra-iscrizioni/${iscrizione._id}/accetta-e-invia-mail`, {
        to_list, subject, body,
      });
      setStep("done");
      onSent && onSent(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore invio mail");
      setStep("compose");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="mail-conferma-modal" style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <span>✉ Anteprima mail conferma — {iscrizione?.cognome} {iscrizione?.nome}</span>
          <button onClick={onClose} data-testid="mc-close" style={closeBtn}>✕</button>
        </div>
        <div style={body_style}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#666" }}>Caricamento preview…</div>
          ) : (
            <>
              {err && (
                <div data-testid="mc-error" style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 3, fontSize: 12, marginBottom: 10 }}>
                  ⚠ {err}
                </div>
              )}
              <Field label="DESTINATARI (separati da virgola)">
                <input data-testid="mc-to" value={toText} onChange={e => setToText(e.target.value)}
                  style={input_style} />
                {sources.length > 0 && (
                  <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>
                    Sorgenti: {sources.join(" · ")}
                  </div>
                )}
              </Field>
              <Field label="OGGETTO">
                <input data-testid="mc-subject" value={subject} onChange={e => setSubject(e.target.value)}
                  style={input_style} />
              </Field>
              <Field label="CORPO (placeholder {link} verrà sostituito con il link sicuro generato)">
                <textarea data-testid="mc-body" value={body} onChange={e => setBody(e.target.value)}
                  rows={18} style={{ ...input_style, fontFamily: "Consolas, monospace", fontSize: 11, resize: "vertical" }} />
              </Field>

              {step === "confirming" && (
                <div data-testid="mc-confirming" style={{ padding: 12, background: "#fff8e1", border: "2px solid #ef6c00", borderRadius: 4, marginTop: 10 }}>
                  <div style={{ fontWeight: 700, color: "#bf360c", marginBottom: 6 }}>
                    ⚠️ CONFERMA OPERAZIONE
                  </div>
                  <div style={{ fontSize: 12, color: "#5d4037", marginBottom: 8, lineHeight: 1.5 }}>
                    Stai per inviare la mail a <b>{toText}</b> e marcare l'iscrizione di <b>{iscrizione?.cognome} {iscrizione?.nome}</b> con stato:<br />
                    → <b style={{ color: "#0d47a1" }}>IN ATTESA PAGAMENTO</b>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button data-testid="mc-back" onClick={() => setStep("compose")} disabled={sending}
                      style={{ padding: "8px 14px", background: "white", border: "1px solid #888", fontSize: 12, fontWeight: 700, cursor: sending ? "wait" : "pointer", borderRadius: 3 }}>
                      ← Modifica
                    </button>
                    <button data-testid="mc-final-send" onClick={invia} disabled={sending}
                      style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: sending ? "wait" : "pointer", borderRadius: 3, letterSpacing: 0.5 }}>
                      {sending ? "Invio…" : "✓ CONFERMA E INVIA"}
                    </button>
                  </div>
                </div>
              )}

              {step === "done" && (
                <div data-testid="mc-done" style={{ padding: 12, background: "#e8f5e9", border: "2px solid #2e7d32", borderRadius: 4, marginTop: 10, color: "#1b5e20", fontWeight: 700 }}>
                  ✅ Mail inviata. Iscrizione in attesa pagamento.
                </div>
              )}
            </>
          )}
        </div>
        {!loading && step === "compose" && (
          <div style={footer}>
            <button onClick={onClose} data-testid="mc-cancel"
              style={{ padding: "8px 14px", background: "white", border: "1px solid #888", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              Annulla
            </button>
            <button onClick={invia} data-testid="mc-next" disabled={!toText.trim() || !subject.trim() || !body.trim()}
              style={{ padding: "8px 18px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3, letterSpacing: 0.5 }}>
              Anteprima OK · Procedi →
            </button>
          </div>
        )}
        {step === "done" && (
          <div style={footer}>
            <button onClick={onClose} data-testid="mc-finish"
              style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3 }}>
              ✓ Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 };
const modal = { background: "white", borderRadius: 6, width: "100%", maxWidth: 820, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" };
const header = { padding: "10px 14px", background: "#0d47a1", color: "white", fontSize: 13, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "6px 6px 0 0" };
const body_style = { padding: 14, overflow: "auto", flex: 1 };
const footer = { padding: "10px 14px", borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa", borderRadius: "0 0 6px 6px" };
const closeBtn = { background: "transparent", border: "none", color: "white", fontSize: 18, cursor: "pointer", padding: "0 6px" };
const input_style = { width: "100%", padding: "7px 10px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box", borderRadius: 3 };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}
